import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styles } from './styles';
import { useEffect, useState } from 'react';
import Button from '@/components/button/Button';
import WarningField from '@/components/warningField/WarningField';
import { RegisterCreditCardController } from './RegisterCreditCardController';
import { CreditCardDTO } from '@/data/DTOs/schedule/CreditCardDTO';
import TextField from '@/components/textField/TextField';
import { useCreditCardService } from '@/data/services/useCreditCardService';
import { AppError } from '@/utils/AppError';
import { IFrameInstamed } from './IFrameInstamed';

export interface RegisterCreditCardPopUpInterface {
  showPopUp: boolean;
  setShowPopUp: (show: boolean) => void;
  setCreditCard: (creditCard: CreditCardDTO) => void;
  locationId: string;
  appointmentTypeId: string;
}

export const RegisterCreditCardPopUp = ({
  showPopUp,
  setShowPopUp,
  setCreditCard,
  locationId,
  appointmentTypeId,
}: RegisterCreditCardPopUpInterface) => {
  const { t } = useTranslation(['creditCard', 'common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [forceReset, setForceReset] = useState(false);
  const [token, setToken] = useState('');
  const [expDate, setExpDate] = useState('');
  const [_, setSendCreateCreditCard] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const { createCreditCard } = useCreditCardService();
  const [registerCreditCard, setRegisterCreditCard] = useState<CreditCardDTO>(
    {}
  );
  const { controlledTextView } = RegisterCreditCardController({
    registerCreditCard,
    setRegisterCreditCard,
    date,
    setDate,
    forceReset,
    setForceReset,
  });

  useEffect(() => {
    if (!showPopUp) {
      setRegisterCreditCard({});
      setDate(null);
      setForceReset(true);
      setLoading(false);
      setShowWarning(false);
      setShowRegister(false);
    }
  }, [showPopUp]);

  useEffect(() => {
    if (showRegister) {
      setToken('');
      setExpDate('');
      setSendCreateCreditCard(true);
    }
  }, [showRegister]);

  const sendCreditCards = async () => {
    setLoading(true);
    setShowWarning(false);

    const dateParts = expDate.split('/');
    if (dateParts.length !== 2) {
      setWarningMessage('Formato de data inválido.');
      setShowWarning(true);
      setLoading(false);
      return;
    }

    const creditCardDTO: CreditCardDTO = {
      creditCardNumber: token,
      expirationMonth: dateParts[0],
      expirationYear: dateParts[1],
      nameOnCard: registerCreditCard.nameOnCard || 'Nome Padrão',
    };

    //console.log('Enviando dados do cartão:', creditCardDTO);

    try {
      createCreditCard(creditCardDTO, locationId, appointmentTypeId)
      .then((creditCardID) => {
        console.log('Cartão salvo com sucesso:', creditCardID);
        setCreditCard(creditCardDTO);
    
        setToken('');
        setExpDate('');
        setRegisterCreditCard({});
        setShowRegister(false);
        setShowPopUp(false);
      })
      .catch((error:AppError) => {
        setWarningMessage(error.message);
        setShowWarning(true);
      });
    
    } catch (error: AppError | any) {
      // setWarningMessage(error.message);
      // setShowWarning(true);
      console.log('Erro ao salvar cartão:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = () => {
    console.log('onSubmit chamado. Token:', token, 'ExpDate:', expDate);

    if (!token || !expDate || !registerCreditCard.nameOnCard?.trim()) {
      console.warn('onSubmit bloqueado: dados insuficientes.');
      setWarningMessage(t('Preencha todos os campos antes de salvar.'));
      setShowWarning(true);
      return;
    }

    sendCreditCards();
  };

  const cardFields = (
    <Stack spacing={2} sx={styles.textsBox}>
      {controlledTextView(
        <TextField
          title={t('screen_credit_card_register_name')}
          placeholder={t('screen_credit_card_register_name')}
          value={registerCreditCard.nameOnCard || ''}
          maxLength={50}
          disabled={loading}
        />,
        'nameOnCard'
      )}
      <Box sx={{ display: 'none' }}>
        {controlledTextView(
          <TextField
            title={t('screen_credit_card_register_number')}
            placeholder={t('screen_credit_card_register_number')}
            value={token}
            maxLength={16}
            disabled={true}
            numberOnly
          />,
          'creditCardNumber'
        )}
        {controlledTextView(
          <TextField
            title={t('screen_credit_card_register_date')}
            placeholder={t('screen_credit_card_register_date')}
            value={expDate}
            maxLength={50}
            disabled={true}
          />,
          'date'
        )}
      </Box>
    </Stack>
  );

  return showPopUp ? (
    <Box sx={styles.overlay}>
      <Box sx={styles.background} />
      <Box sx={styles.container} onClick={(e) => e.stopPropagation()}>
        <Typography sx={styles.title}>
          {t('screen_credit_card_register_title')}
        </Typography>
        <Typography sx={styles.description}>
          {t('screen_credit_card_register_description')}
        </Typography>

        {cardFields}
        <Box sx={{ width: '100%' }}>
          <WarningField
            description={warningMessage}
            isHidden={!showWarning}
            setShowWarning={setShowWarning}
            status="warning"
            rightButtonTitle={t('common:close')}
            rightButtonAction={() => {
              setShowWarning(false);
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Button
            title={t('screen_credit_card_register_number_btn')}
            onlyText
            onClick={() => {
              setShowRegister(true);
            }}
            disable={!!token && !!expDate}
          />
        </Box>

        <Box sx={styles.buttonContent}>
          <Button
            title={t('common:save')}
            onClick={() => {
              onSubmit();
            }}
            disable={
              loading ||
              !registerCreditCard.nameOnCard?.trim() ||
              !token ||
              !expDate
            }
            loading={loading}
          />
          <Button
            title={t('common:cancel')}
            outlined
            onClick={() => {
              setShowPopUp(false);
              setToken('');
              setExpDate('');
              setRegisterCreditCard({});
              setShowRegister(false);
            }}
            disable={loading}
          />
        </Box>
      </Box>
      <IFrameInstamed
        setShowIFrame={setShowRegister}
        showIFrame={showRegister}
        setToken={setToken}
        setExpDate={setExpDate}
      />
    </Box>
  ) : (
    <></>
  );
};
